import React from "react";
import {
    Paper,
    Box,
    Typography,
    Button,
    List,
    ListItem,
    ListItemText,
    ListItemIcon,
    ListItemSecondaryAction,
    IconButton,
    Chip,
    LinearProgress,
    Tooltip,
} from "@mui/material";

// Components
import Icon from "@/theme/vectors/bundle";

// Utils
import { getMediaTypeIcon, formatFileSize, getFileTypeDisplay } from "@/utils/products/mediaHelpers";
import { getFileTypeCategory, VALID_FILENAME_PATTERN } from "@/utils/products/constants";

const FileUpload = ({ 
    selectedFiles, 
    uploading, 
    onFileUpload, 
    onCancelUpload, 
    onClearSelectedFiles,
    t
}) => {
    return (
        <Paper sx={{ mt: 2, mb: 3, p: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="subtitle1">
                    {t('product.detail.selectedFiles', 'Selected Files')} ({selectedFiles.length})
                </Typography>
                <Box>
                    <Button 
                        variant="contained"
                        color="primary"
                        disabled={uploading || selectedFiles.length === 0}
                        onClick={onFileUpload}
                        sx={{ 
                            mr: 1, 
                            bgcolor: '#8D6E63',
                            '&:hover': {
                                bgcolor: '#6D4C41'
                            },
                            textTransform: 'uppercase'
                        }}
                    >
                        {t('product.detail.uploadFiles', 'UPLOAD FILES')}
                    </Button>
                    <Button 
                        variant="outlined"
                        disabled={uploading}
                        onClick={onClearSelectedFiles}
                        startIcon={<Icon name="remove" />}
                        sx={{ 
                            borderColor: '#8D6E63',
                            color: '#8D6E63',
                            '&:hover': {
                                borderColor: '#6D4C41'
                            }
                        }}
                    >
                        {t('product.detail.clear', 'Xóa')}
                    </Button>
                </Box>
            </Box>
            
            <List>
                {selectedFiles.map((fileObj) => (
                    <ListItem
                        key={fileObj.id}
                        sx={{
                            border: '1px solid',
                            borderColor: 'divider',
                            borderRadius: 1,
                            mb: 1,
                            py: 1,
                            bgcolor: fileObj.status === 'error' ? 'rgba(244, 67, 54, 0.05)' : 
                                   fileObj.status === 'completed' ? 'rgba(76, 175, 80, 0.05)' : 
                                   'transparent'
                        }}
                    >
                        <ListItemIcon sx={{ minWidth: 56, pr: 2 }}>
                            <Box 
                                sx={{ 
                                    width: 36, 
                                    height: 36, 
                                    display: 'flex', 
                                    alignItems: 'center', 
                                    justifyContent: 'center',
                                    border: '1px solid',
                                    borderColor: 'divider',
                                    borderRadius: 1
                                }}
                            >
                                {getMediaTypeIcon(fileObj.file.type)}
                            </Box>
                        </ListItemIcon>
                        <ListItemText
                            primary={
                                <Typography 
                                    variant="body1" 
                                    noWrap
                                    color={fileObj.invalidFilename ? "error" : "textPrimary"}
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        '& .invalid-icon': {
                                            ml: 1,
                                            color: 'error.main'
                                        }
                                    }}
                                >
                                    {fileObj.file.name}
                                    {fileObj.invalidFilename && (
                                        <Tooltip title="Invalid filename format">
                                            <Icon name="error" size={16} className="invalid-icon" />
                                        </Tooltip>
                                    )}
                                </Typography>
                            }
                            secondary={
                                <Box sx={{ display: 'flex', flexDirection: 'column', mt: 0.5 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                                        <Chip 
                                            size="small" 
                                            label={typeof getFileTypeDisplay === 'function' 
                                                ? getFileTypeDisplay(fileObj.file.type, t) 
                                                : fileObj.file.type}
                                            sx={{ mr: 1, height: 20, fontSize: '0.75rem' }} 
                                            variant="outlined"
                                        />
                                        <Chip 
                                            size="small" 
                                            label={formatFileSize(fileObj.file.size)} 
                                            sx={{ mr: 1, height: 20, fontSize: '0.75rem' }} 
                                            variant="outlined"
                                        />
                                        <Chip 
                                            size="small" 
                                            label={
                                                fileObj.status === 'pending' ? t('product.detail.pending', 'Pending') :
                                                fileObj.status === 'uploading' ? t('product.detail.uploading', 'Uploading') :
                                                fileObj.status === 'completed' ? t('product.detail.completed', 'Completed') :
                                                fileObj.invalidFilename ? t('product.detail.invalidName', 'Invalid Name') :
                                                t('product.detail.error', 'Error')
                                            }
                                            sx={{ 
                                                height: 20, 
                                                fontSize: '0.75rem',
                                                bgcolor: fileObj.invalidFilename ? 'error.light' : undefined,
                                                color: fileObj.invalidFilename ? 'white' : undefined
                                            }}
                                            variant={fileObj.invalidFilename ? "filled" : "outlined"}
                                        />
                                    </Box>
                                    <Box sx={{ width: '100%', mt: 1 }}>
                                        <LinearProgress 
                                            variant="determinate" 
                                            value={fileObj.progress} 
                                            color={
                                                fileObj.status === 'error' ? 'error' :
                                                fileObj.status === 'completed' ? 'success' : 'primary'
                                            }
                                            sx={{ 
                                                height: 4, 
                                                borderRadius: 2,
                                                backgroundColor: '#f5f5f5'
                                            }}
                                        />
                                        <Box 
                                            sx={{ 
                                                display: 'flex', 
                                                justifyContent: 'flex-end', 
                                                mt: 0.5
                                            }}
                                        >
                                            <Typography 
                                                variant="caption" 
                                                color="textSecondary"
                                            >
                                                {fileObj.progress}%
                                            </Typography>
                                        </Box>
                                    </Box>
                                    {fileObj.error && (
                                        <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
                                            {fileObj.error}
                                        </Typography>
                                    )}
                                </Box>
                            }
                        />
                        <ListItemSecondaryAction>
                            {fileObj.status === 'pending' && (
                                <IconButton 
                                    edge="end" 
                                    onClick={() => onCancelUpload(fileObj.id)}
                                    title={t('product.detail.remove', 'Remove')}
                                    size="small"
                                >
                                    <Icon name="close" size={16} />
                                </IconButton>
                            )}
                            {fileObj.status === 'completed' && (
                                <IconButton 
                                    edge="end" 
                                    href={fileObj.result?.data?.accessUrl} 
                                    target="_blank"
                                    title={t('product.detail.view', 'View')}
                                    size="small"
                                >
                                    <Icon name="view" size={16} />
                                </IconButton>
                            )}
                            {fileObj.status === 'error' && (
                                <IconButton 
                                    edge="end" 
                                    onClick={() => onCancelUpload(fileObj.id)}
                                    title={t('product.detail.remove', 'Remove')}
                                    color="error"
                                    size="small"
                                >
                                    <Icon name="close" size={16} />
                                </IconButton>
                            )}
                        </ListItemSecondaryAction>
                    </ListItem>
                ))}
            </List>
        </Paper>
    );
};

export default FileUpload;